import React, { createContext, useContext, useState } from 'react';

/** Mirrors the `kpis` shape SubmissionCenterPage already computes — a ready-to-render
 *  array rather than named fields, so the page doesn't need a second representation. */
export interface SubmissionCenterKpi {
  label: string;
  value: string;
  foot: string;
  tone?: string;
}

/** The live opportunities list identifies a quote/policy by its NowCerts databaseId —
 *  QuotePolicyDetailPanel fetches everything else itself from that id. A 'notes'
 *  selection is an opportunity's own note thread (GET .../opportunity/{id}/notes),
 *  shown in the same right-rail slot via OpportunityNotesPanel. */
export type SubmissionCenterSelection =
  // opportunityId is optional only because this selection has one call site
  // today (SubmissionCenterPage's quotes table) — it's always passed there,
  // needed so the panel's compose box can log communication history against
  // the right opportunity, not just the quote/policy itself.
  | { kind: 'policy'; policyId: string; opportunityId?: string }
  | { kind: 'notes'; opportunityId: string ;insuredDatabaseId: string};

interface SubmissionCenterStatsContextValue {
  kpis: SubmissionCenterKpi[];
  setKpis: (kpis: SubmissionCenterKpi[]) => void;
  /** The submission a user clicked into — shown in the right rail in place of
   *  the KPI stats, rather than a separate floating drawer. */
  selected: SubmissionCenterSelection | null;
  setSelected: (s: SubmissionCenterSelection | null) => void;
  // "Create task" is one click, but there is no tasks API to create it in yet —
  // this records the click so the action still feels real rather than
  // pretending to write somewhere it can't reach.
  createdTasks: Set<string>;
  createTask: (key: string) => void;
  /** Bumped whenever a quote is successfully created from the standalone Add
   *  Quote page — SubmissionCenterPage watches this to refresh just the
   *  affected opportunity's quotes, since the page has no direct reference
   *  back to the list it was opened from. */
  quoteAddedSignal: number;
  /** Which opportunity/number the last successful add-quote was for — lets
   *  SubmissionCenterPage refresh just that opportunity's quotes-policies list
   *  (with the new number appended) instead of the whole opportunities list,
   *  since the opportunity's own policyNumbers field may not reflect the new
   *  quote yet. */
  lastQuoteAdded: { opportunityId: string; quoteNumber: string } | null;
  notifyQuoteAdded: (info: { opportunityId: string; quoteNumber: string }) => void;
}

const SubmissionCenterStatsContext = createContext<SubmissionCenterStatsContextValue>({
  kpis: [],
  setKpis: () => {},
  selected: null,
  setSelected: () => {},
  createdTasks: new Set(),
  createTask: () => {},
  quoteAddedSignal: 0,
  lastQuoteAdded: null,
  notifyQuoteAdded: () => {},
});

export const SubmissionCenterStatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [kpis, setKpis] = useState<SubmissionCenterKpi[]>([]);
  const [selected, setSelected] = useState<SubmissionCenterSelection | null>(null);
  const [createdTasks, setCreatedTasks] = useState<Set<string>>(new Set());
  const createTask = (key: string) => setCreatedTasks(prev => new Set(prev).add(key));
  const [quoteAddedSignal, setQuoteAddedSignal] = useState(0);
  const [lastQuoteAdded, setLastQuoteAdded] = useState<{ opportunityId: string; quoteNumber: string } | null>(null);
  const notifyQuoteAdded = (info: { opportunityId: string; quoteNumber: string }) => {
    setLastQuoteAdded(info);
    setQuoteAddedSignal(n => n + 1);
  };

  return (
    <SubmissionCenterStatsContext.Provider value={{ kpis, setKpis, selected, setSelected, createdTasks, createTask, quoteAddedSignal, lastQuoteAdded, notifyQuoteAdded }}>
      {children}
    </SubmissionCenterStatsContext.Provider>
  );
};

export const useSubmissionCenterStats = () => useContext(SubmissionCenterStatsContext);
